/*
 * File: cell-action-edit.ts
 * Created: 2025-12-07 12:xx:xx
 * Author: Val Liu <valuis0429@gmail.com>
 * -----
 * Last Modified: 2026-01-16 08:24:43
 * Modified By: Val Liu <valuis0429@gmail.com>
 */

// directives/cell-action-edit.ts
import { VNode, DirectiveOptions } from "vue";
import IvAutoModal from "@root/components/modals/auto-modal/auto-modal";
import IvForm from "@root/components/form/core/form";
import { IQuickUnit, QuickHelper } from "@root/core/utilities/quick-helper";
import { withMutedPropWarn } from "@root/core/utilities/with-muted-prop-warn";

/**
 * cell-action-edit
 * - directive 掛在任何 DOM
 * - click 後 programmatic mount IvAutoModal + IvForm 到 body
 * - 使用 QuickHelper.forTableU(quick, id)
 * - IvForm updated -> 關閉 modal + 可回呼 updated
 */

type BindingValue =
    | any
    | {
          quick: IQuickUnit;
          id: any;
          label?: string;
          size?: string;
          updated?: (...args: any[]) => void;
      };

function normalizeBinding(value: BindingValue) {
    if (value && typeof value === "object" && "quick" in value) {
        return {
            quick: (value as any).quick,
            id: (value as any).id,
            label: (value as any).label as string | undefined,
            size: (value as any).size as string | undefined,
            updated: (value as any).updated as ((...args: any[]) => void) | undefined,
        };
    }

    // 若有人誤用 v-cell-action-edit="quicks().User"
    // 這裡會讓 id 缺失，後面會被 guard 擋掉
    return {
        quick: value,
        id: undefined,
        label: undefined,
        size: undefined,
        updated: undefined,
    };
}

/** ---------- module-level state ---------- */
const handlers = new WeakMap<HTMLElement, (e: MouseEvent) => void>();
const latestValues = new WeakMap<HTMLElement, BindingValue>();

function openEditModal(opts: {
    quick: any;
    id: any;
    label: string;
    size: string;
    updated?: (...args: any[]) => void;
}) {
    const { quick, id, label, size, updated } = opts;

    const mountEl = document.createElement("div");
    document.body.appendChild(mountEl);

    let vm: any = null;

    const destroy = () => {
        if (vm) {
            vm.$destroy();
            vm = null;
        }
        if (mountEl.parentNode) {
            mountEl.parentNode.removeChild(mountEl);
        }
    };

    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const VueCtor = require("vue").default || require("vue");

    vm = new VueCtor({
        render(h: any) {
            const handleUpdated = (...args: any[]) => {
                try {
                    updated?.(...args);
                } catch (e) {
                    // eslint-disable-next-line no-console
                    console.error("[v-cell-action-edit] updated error:", e);
                }
                destroy();
            };

            const handleVisibleUpdate = (v: boolean) => {
                if (!v) destroy();
            };

            return h(
                IvAutoModal,
                {
                    props: {
                        visible: true,
                        label,
                        size,
                    },
                    on: {
                        "update:visible": handleVisibleUpdate,
                    },
                },
                [
                    h(IvForm, {
                        props: {},
                        attrs: {
                            // ✅ edit 的核心：帶 id
                            ...QuickHelper.forTableU(quick, id),
                        },
                        on: {
                            updated: handleUpdated,
                        },
                    }),
                ],
            );
        },
    });

    vm.$mount(mountEl);

    return { destroy };
}

function resolveLabel(bindingValue: BindingValue, vnode?: VNode) {
    const n = normalizeBinding(bindingValue);
    if (n.label) return n.label;
    let quick = n.quick;

    const labelFromProps = (vnode?.componentOptions?.propsData as any)?.label;
    if (labelFromProps) return labelFromProps;

    return `{w_QuickList_EditItem("${quick.title}")}`;
}

function applyAutoLabel(bindingValue: BindingValue, vnode?: VNode) {
    if (!vnode || !vnode.componentInstance) return;

    const vm: any = vnode.componentInstance;
    const propsOptions = (vm.$options && vm.$options.props) || {};
    const hasLabelProp = Object.prototype.hasOwnProperty.call(propsOptions, "label");

    // 這個元件根本沒有 label prop，就不用管了
    if (!hasLabelProp) return;

    // 呼叫端有明確傳 label，就不要覆蓋
    const propsData = (vnode.componentOptions && vnode.componentOptions.propsData) || {};
    if (Object.prototype.hasOwnProperty.call(propsData, "label")) {
        return;
    }

    // 真的「沒有設定 prop label」→ 自動套用 resolveLabel
    const label = resolveLabel(bindingValue, vnode);

    // 這裡直接寫到子元件的 prop 上（雖然是 hack，但在 directive 情境可接受）
    withMutedPropWarn(() => (vm.label = label));
    vm.$forceUpdate();
}

function resolveSize(bindingValue: BindingValue) {
    const n = normalizeBinding(bindingValue);
    return n.size || "lg";
}

const CellActionEditDirective: DirectiveOptions = {
    bind(el: HTMLElement, binding: any, vnode: VNode) {
        latestValues.set(el, binding.value);

        const handler = (e: MouseEvent) => {
            // 阻止 bubble up
            e.stopPropagation();
            e.stopImmediatePropagation?.();

            // 只在 <a> 才 preventDefault，避免 href="#" 或連結預設行為
            if (el instanceof HTMLAnchorElement) {
                e.preventDefault();
            }

            const latest = latestValues.get(el) ?? binding.value;
            const n = normalizeBinding(latest);

            // ✅ 必要 guard：沒有 id 就不要開
            if (n.id === undefined || n.id === null || n.id === "") {
                // 你要更嚴格也可以 console.warn
                // eslint-disable-next-line no-console
                console.warn("[v-cell-action-edit] missing id, modal not opened.");
                return;
            }

            const label = resolveLabel(latest, vnode);
            const size = resolveSize(latest);

            openEditModal({
                quick: n.quick,
                id: n.id,
                label,
                size,
                updated: n.updated,
            });
        };

        handlers.set(el, handler);
        el.addEventListener("click", handler);
        // 初次掛載時試著幫目標元件自動補 label
        applyAutoLabel(binding.value, vnode);
    },

    update(el: HTMLElement, binding: any, vnode: VNode) {
        latestValues.set(el, binding.value);
        // 綁定值變動時（比如 quick / label 改了），同步更新元件的預設 label
        applyAutoLabel(binding.value, vnode);
    },

    unbind(el: HTMLElement) {
        const handler = handlers.get(el);
        if (handler) {
            el.removeEventListener("click", handler);
        }
        handlers.delete(el);
        latestValues.delete(el);
    },
};

export default CellActionEditDirective;
